import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useAvatarThree } from '../avatar'
import {
  SceneAtmosphere,
  BootSequence,
  ChapterWipe,
  SceneChrome,
  SceneHero,
  CommandDock,
} from './shell'
import { LandingOverlay } from './panels/LandingOverlay'
import { OnboardingChecklist } from './panels/OnboardingChecklist'
import { useSceneStore } from './sceneStore'
import {
  useAgentSession,
  useBootSequence,
  useChapterWipe,
  useRealtimeStages,
  useSceneFromAgent,
  useSceneUrlSync,
  useVoiceInput,
} from './hooks'
import { chapterMap } from './constants/sceneConstants'
import { extractLatestUiAction, extractPanelDrafts, getLatestMessage } from './utils/agentMessageUtils'
import {
  IDLE_ACTIVITY,
  THINKING_ACTIVITY,
  mapRealtimeEvent,
  resolveAgentActivity,
} from './utils/agentActivity'
import { sessionRouteId } from './utils/sessionRouting'
import { stripUrls } from './utils/oauthConnect'
import { userSpeakerLabel } from './utils/userLabel'
import '../../assets/aria-console.css'

export default function AriaSceneRoot() {
  const location = useLocation()
  const { user, isAuthenticated } = useAuthStore()
  const { layout, panel, flickTrigger, openPanel, landingDismissed, showLanding } = useSceneStore()
  const { closePanelAndUrl } = useSceneUrlSync()

  const avatarApiRef = useRef({ setState: () => {}, setMouse: () => {} })
  const cmdRef = useRef(null)
  const toastTimerRef = useRef(null)
  const giantLRef = useRef(null)
  const cursorRef = useRef(null)
  const hasSpokenIntroRef = useRef(false)

  const [displayAssistantText, setDisplayAssistantText] = useState('')
  const [toastMsg, setToastMsg] = useState('')
  const [realtimeMessage, setRealtimeMessage] = useState('')
  const [realtimeActivity, setRealtimeActivity] = useState(IDLE_ACTIVITY)
  const [publishEvent, setPublishEvent] = useState(null)

  const {
    session,
    setSession,
    loading,
    busy,
    stage,
    setStage,
    refreshSession,
    submitMessage,
    sendSensitiveInput,
    startNewSession,
    openSession,
  } = useAgentSession()

  const { chapter, wipeState, goChapter } = useChapterWipe('intake')
  const boot = useBootSequence(loading)

  const voice = useVoiceInput({ session, busy, setStage, setSession })

  const panelDrafts = useMemo(() => extractPanelDrafts(session), [session])
  const rawUiAction = useMemo(() => extractLatestUiAction(session?.messages), [session?.messages])
  const latestUiAction = useMemo(() => {
    if (isAuthenticated && rawUiAction?.type === 'auth_redirect') return null
    return rawUiAction
  }, [rawUiAction, isAuthenticated])
  const speakerLabel = useMemo(() => userSpeakerLabel(isAuthenticated, user), [isAuthenticated, user])
  const latestUser = getLatestMessage(session?.messages, 'user')
  const latestAssistant = getLatestMessage(session?.messages, 'assistant')
  const assistantDisplayContent = useMemo(() => {
    const text = latestAssistant?.content || ''
    if (latestUiAction?.type === 'oauth_connect') return stripUrls(text)
    if (latestUiAction?.type === 'user_choice') {
      const prompt = latestUiAction.prompt
      if (prompt && text.length > 120) return prompt
    }
    if (latestUiAction?.type === 'context_select') {
      return latestUiAction.reason || 'Pick a repository below.'
    }
    if (latestUiAction?.type === 'draft_review') {
      return latestUiAction.prompt || 'Review the draft below.'
    }
    return text
  }, [latestAssistant?.content, latestUiAction])
  const chapterCfg = chapterMap[chapter]

  const usageText = session?.usage
    ? `${session.usage.total_input_tokens || 0} / ${session.usage.plan_token_limit || '∞'}`
    : '0 / 2M'
  const usagePct = session?.usage?.plan_token_limit
    ? Math.min(99, ((session.usage.total_input_tokens || 0) / session.usage.plan_token_limit) * 100)
    : 7

  useSceneFromAgent(latestUiAction, sessionRouteId(session), panelDrafts)

  useAvatarThree(loading, avatarApiRef)

  useEffect(() => {
    document.body.classList.add('aria-console-active')
    return () => document.body.classList.remove('aria-console-active')
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (location.pathname === '/' && !landingDismissed && !params.get('panel')) {
      showLanding()
    }
  }, [location.pathname, location.search, landingDismissed, showLanding])

  const showToast = useCallback((msg) => {
    setToastMsg(msg)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToastMsg(''), 2600)
  }, [])

  const INTRO_LINE = "I'm online. Tell me what to ship - talk, or type below."

  const handleSubmit = useCallback((raw) => {
    submitMessage(raw, { onChapterChange: goChapter })
    voice.setInputText('')
  }, [submitMessage, goChapter, voice])

  const handleRealtimeStage = useCallback((event) => {
    setRealtimeMessage(event.message || '')
    const activity = mapRealtimeEvent(event)
    if (event.stage === 'thinking' || event.stage === 'tool' || event.stage === 'drafting') {
      setRealtimeActivity(activity.active ? activity : THINKING_ACTIVITY)
      setStage('thinking')
      goChapter('index')
    }
    // Keep the last activity visible until the HTTP turn finishes (busy → false).
    if (event.stage === 'completed') setStage('idle')
    if (event.stage === 'failed') {
      setRealtimeActivity(IDLE_ACTIVITY)
      setStage('idle')
    }
    if (
      event.stage === 'tool_call'
      || event.stage === 'tool_result'
      || event.stage === 'failed'
    ) {
      if (event.data?.draft_id || /creat(e|ing).*(jira|asana|issue|task)/i.test(event.message || '')) {
        setPublishEvent({ ...event, receivedAt: Date.now() })
      }
    }
  }, [setStage, goChapter])

  useEffect(() => {
    if (busy) {
      setRealtimeActivity((prev) => (prev.active ? prev : THINKING_ACTIVITY))
      return undefined
    }
    setRealtimeActivity(IDLE_ACTIVITY)
    return undefined
  }, [busy])

  useRealtimeStages(session?.id, {
    onStage: handleRealtimeStage,
    onRefresh: refreshSession,
  })

  useEffect(() => {
    const hasPublished = panelDrafts.some((d) => d.published)
    if (hasPublished) goChapter('ship')
    else if (panelDrafts.some((d) => !d.published)) goChapter('draft')
  }, [panelDrafts, goChapter])

  useEffect(() => {
    avatarApiRef.current.setState(stage)
  }, [stage])

  useEffect(() => {
    hasSpokenIntroRef.current = false
  }, [session?.id])

  useEffect(() => {
    const text = assistantDisplayContent
    if (!text) {
      setDisplayAssistantText('')
      return undefined
    }
    let i = 0
    setDisplayAssistantText('')
    const iv = setInterval(() => {
      i += 1
      setDisplayAssistantText(text.slice(0, i))
      if (i >= text.length) clearInterval(iv)
    }, 18)
    return () => clearInterval(iv)
  }, [latestAssistant?.id, assistantDisplayContent])

  useEffect(() => {
    if (loading || !boot.stageLive) {
      window.speechSynthesis?.cancel()
      return undefined
    }
    if (!assistantDisplayContent || !window.speechSynthesis) return undefined
    hasSpokenIntroRef.current = true
    return voice.speakWithSelectedVoice(assistantDisplayContent)
  }, [loading, boot.stageLive, latestAssistant?.id, assistantDisplayContent, voice.speakWithSelectedVoice])

  useEffect(() => {
    if (loading || !boot.stageLive || !session?.id || latestAssistant?.content || hasSpokenIntroRef.current) {
      return undefined
    }

    let introStarted = false
    const speakIntro = () => {
      if (introStarted || hasSpokenIntroRef.current || latestAssistant?.content) return
      voice.speakWithSelectedVoice(INTRO_LINE, {
        preview: true,
        onStart: () => {
          introStarted = true
          hasSpokenIntroRef.current = true
        },
      })
    }

    const timer = setTimeout(speakIntro, 300)
    const onInteract = () => speakIntro()
    window.addEventListener('pointerdown', onInteract)
    window.addEventListener('keydown', onInteract)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', onInteract)
      window.removeEventListener('keydown', onInteract)
    }
  }, [loading, boot.stageLive, session?.id, latestAssistant?.content, voice.speakWithSelectedVoice])

  useEffect(() => {
    if (loading) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const finePointer = window.matchMedia('(pointer: fine)').matches
    const onPointerMove = (event) => {
      const nx = (event.clientX / window.innerWidth - 0.5) * 2
      const ny = (event.clientY / window.innerHeight - 0.5) * 2
      avatarApiRef.current.setMouse(nx * 0.5, ny * 0.5)
      if (!reduce && giantLRef.current) {
        giantLRef.current.style.transform = `translateY(-52%) translate(${nx * -7}px, ${ny * 3.5}px)`
      }
      if (!reduce && finePointer && cursorRef.current) {
        cursorRef.current.style.left = `${event.clientX}px`
        cursorRef.current.style.top = `${event.clientY}px`
      }
    }
    window.addEventListener('mousemove', onPointerMove)
    return () => window.removeEventListener('mousemove', onPointerMove)
  }, [loading])

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
  }, [])

  const isUserSpeaking = voice.voiceActive || Boolean(voice.voiceTranscript.trim())
  const assistantIsLatest = !isUserSpeaking && Boolean(latestAssistant?.content)
    && (!latestUser?.id || latestAssistant.id > latestUser.id)
  const isUserTurn = isUserSpeaking || (!assistantIsLatest && Boolean(latestUser?.content))
  const activeWho = isUserTurn ? speakerLabel : 'ARIA'
  const activeText = isUserSpeaking
    ? voice.voiceTranscript
    : assistantIsLatest
      ? (displayAssistantText || assistantDisplayContent)
      : (latestUser?.content || (boot.stageLive ? INTRO_LINE : ''))
  const prevText = isUserTurn
    ? (latestAssistant?.content || '')
    : (latestUser?.content || realtimeMessage || '')

  const agentActivity = useMemo(
    () => resolveAgentActivity({
      voiceActive: voice.voiceActive,
      busy,
      realtimeActivity,
      publishing: false,
    }),
    [voice.voiceActive, busy, realtimeActivity],
  )
  const chromeStageLabel = agentActivity.active
    ? agentActivity.chromeLabel
    : stage.toUpperCase()

  const hideSuggs = busy
    || voice.voiceActive
    || Boolean(voice.voiceTranscript.trim())
    || stage === 'listening'
    || stage === 'speaking'
    || stage === 'thinking'
    || Boolean(displayAssistantText)
    || Boolean(latestAssistant?.content)
    || Boolean(latestUiAction)
    || Boolean(panel)
    || chapter !== 'intake'

  const sceneProps = {
    drafts: panelDrafts,
    session,
    showToast,
    goChapter,
    closePanelAndUrl,
    publishEvent,
    setSession,
    sendSensitiveInput: (field, value) => sendSensitiveInput(field, value, { onChapterChange: goChapter }),
    onUserChoice: (message) => handleSubmit(message),
    uiAction: latestUiAction,
    openSession,
    startNewSession,
  }

  if (loading) {
    return <div className="aria-loading">Starting ARIA...</div>
  }

  return (
    <div className="aria-console-root">
      <SceneAtmosphere flickTrigger={flickTrigger} />
      <div className="cursor-cross" ref={cursorRef} id="cursor" aria-hidden="true" />

      {panel === 'landing' ? (
        <LandingOverlay
          sessionRouteId={sessionRouteId(session)}
          onEnterWithoutSession={startNewSession}
        />
      ) : null}

      <BootSequence
        bootVisible={boot.bootVisible && panel !== 'landing'}
        bootExiting={boot.bootExiting}
        bootTermVisible={boot.bootTermVisible}
        bootLineCount={boot.bootLineCount}
        onFinish={boot.finishBoot}
      />

      <main
        id="stage"
        data-chapter={chapter}
        className={`${boot.stageLive ? 'live' : ''} ${hideSuggs ? 'suggs-hidden' : 'suggs-visible'} ${panel && panel !== 'landing' ? 'panel-open' : ''}`}
      >
        <ChapterWipe wipeState={wipeState} chapterCfg={chapterCfg} />
        <div className="canvas">
          <div className="splash s1" />
          <div className="splash s2" />
          <div className="splash s3" />
        </div>
        <div className={`giant-num left ${stage === 'listening' || stage === 'reading' ? 'hot' : ''}`} ref={giantLRef}>
          {chapterCfg.num}
        </div>
        <div className="giant-num right">{chapterCfg.code}</div>
        <span className="vert-label l">{chapterCfg.left}</span>
        <span className="vert-label r">{chapterCfg.right}</span>
        <div className="chapter-tag">CHAPTER <b>{chapterCfg.name}</b></div>
        <div className="brand-lockup" aria-hidden="true">
          <div className="name">ARIA</div>
          <div className="tag">ticket intelligence for operators</div>
        </div>

        <OnboardingChecklist session={session} />

        <SceneChrome
          layout={layout}
          stage={stage}
          stageLabel={chromeStageLabel}
          usageText={usageText}
          usagePct={usagePct}
          isAuthenticated={isAuthenticated}
          availableVoices={voice.availableVoices}
          selectedVoiceURI={voice.selectedVoiceURI}
          onVoiceChange={(event) => {
            voice.setSelectedVoiceURI(event.target.value)
            localStorage.setItem('aria_selected_voice_uri', event.target.value)
            showToast('VOICE UPDATED')
            setTimeout(() => voice.speakWithSelectedVoice('Voice changed. This is a preview.', { preview: true }), 80)
          }}
          onIntegrations={() => openPanel('integrations')}
          onContext={() => openPanel('context', { payload: { mode: 'import', reason: 'Import a GitHub issue or pick a repository' } })}
          onSignIn={() => openPanel('auth', { mode: 'login' })}
          onSessions={() => openPanel('sessions')}
          onNewSession={() => startNewSession().then(() => showToast('NEW SESSION'))}
          onUsage={() => openPanel('billing')}
          onTickets={() => openPanel('tickets')}
        />

        <SceneHero
          layout={layout}
          stage={stage}
          voiceActive={voice.voiceActive}
          toastMsg={toastMsg}
          prevText={prevText}
          activeWho={activeWho}
          activeText={activeText}
          isUserTurn={isUserTurn}
          uiAction={latestUiAction}
          agentActivity={agentActivity}
          sceneProps={sceneProps}
        />

        <CommandDock
          hideSuggs={hideSuggs}
          busy={busy}
          agentActivity={agentActivity}
          mode={voice.mode}
          voiceActive={voice.voiceActive}
          inputText={voice.inputText}
          onInputChange={voice.setInputText}
          onSubmit={handleSubmit}
          onFocus={() => !busy && setStage('reading')}
          onBlur={() => !busy && setStage('idle')}
          onTextMode={() => { voice.stopVoice(); cmdRef.current?.focus() }}
          onVoiceToggle={() => (voice.voiceActive ? voice.stopVoice() : voice.startVoice())}
          cmdRef={cmdRef}
        />
      </main>
    </div>
  )
}
